import { SUBJECT_CATEGORY_CODES, type SubjectCategoryCode } from '@athena/shared'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  Checkbox,
  CollapsibleSection,
  ConfirmationDialog,
  ErrorState,
  FailureNotice,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  TextInput,
  useLanguage,
  usePageTitle,
  useToast,
  useTranslate,
} from '@athena/ui'
import {
  adminCreateSubjectInput,
  adminUpdateSubjectInput,
  adminDeleteSubjectInput,
} from '@athena/validation'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ManagedSubjectDocument,
  ManagedSubjectsDocument,
  CreateManagedSubjectDocument,
  UpdateManagedSubjectDocument,
  DeleteManagedSubjectDocument,
  type ManagedSubjectFieldsFragment,
} from '../../graphql/generated/operations.js'
import { SubjectStatusTag } from '../display/Tags.js'
import { countOf } from '../../lib/display.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'
import { fieldErrors, useFocusFirstInvalid } from '../../lib/validation.js'
import { ReasonField, UnsavedChanges } from './ManagementSupport.js'
import { ResourceAuditLog } from './ResourceAuditLog.js'
export function SubjectScreen() {
  const t = useTranslate()
  const { subjectId } = useParams({ from: '/$language/subjects/$subjectId' })
  const [saved, setSaved] = useState(false)
  const { data, loading, error } = useQuery(ManagedSubjectDocument, {
    variables: { subjectId },
    fetchPolicy: 'cache-and-network',
  })
  const language = useLanguage()
  // The subject's name in the page's language once loaded, and "Subject"
  // until then. The code is beside it on the page.
  usePageTitle(
    data
      ? language === 'en'
        ? data.adminSubject.englishName
        : data.adminSubject.spanishName
      : t('practice.fields.subject'),
  )
  if (!data && loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error) return <ErrorState>{t('admin.loadError')}</ErrorState>
  return data ? (
    <SubjectForm
      key={`${subjectId}:${data.adminSubject.version}`}
      subject={data.adminSubject}
      onSaved={() => setSaved(true)}
      // Here rather than in the form's own state: it remounts on every save
      // and would take its own confirmation with it.
      confirmation={saved ? <InlineConfirmation>{t('management.saved')}</InlineConfirmation> : null}
    />
  ) : null
}
export function NewSubjectScreen() {
  const t = useTranslate()
  usePageTitle(t('management.createSubject'))
  return <SubjectForm />
}
function SubjectForm({
  subject,
  onSaved,
  confirmation,
}: {
  subject?: ManagedSubjectFieldsFragment
  onSaved?: () => void
  confirmation?: ReactNode
}) {
  const t = useTranslate()
  const userError = useFirstUserErrorText()
  const navigate = useNavigate()
  const { language } = useParams({ from: '/$language' })
  const initial = {
    code: subject?.code ?? '',
    spanishName: subject?.spanishName ?? '',
    englishName: subject?.englishName ?? '',
    active: subject?.active ?? true,
    categoryCodes: (subject?.categoryCodes ?? []) as SubjectCategoryCode[],
    reason: '',
  }
  const [values, setValues] = useState(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  /**
   * A save that never got an answer. No retry of its own: sending it again is
   * pressing Save again, with the reason and every value still filled in.
   */
  const [failure, setFailure] = useState<unknown>()
  const [success, setSuccess] = useState(false)
  const [destination, setDestination] = useState<string | null>(null)
  const toast = useToast()
  /** Deleting, which is asked in its own dialog with its own reason. */
  const [deleting, setDeleting] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteOutcome, setDeleteOutcome] = useState<{ refusal: string } | { error: unknown }>()
  const [busy, setBusy] = useState(false)
  const lock = useRef(false)
  const invalid = useFocusFirstInvalid<HTMLFormElement>()
  const refresh = { refetchQueries: [ManagedSubjectsDocument], awaitRefetchQueries: true }
  const [create] = useMutation(CreateManagedSubjectDocument, refresh)
  const [update] = useMutation(UpdateManagedSubjectDocument, refresh)
  const [remove] = useMutation(DeleteManagedSubjectDocument, refresh)
  useEffect(() => {
    // Both leave this form behind, so both are confirmed where they land.
    if (destination === 'list')
      void navigate({ to: '/$language/subjects', params: { language } }).then(() =>
        toast.show(t('management.subjectDeleted')),
      )
    else if (destination)
      void navigate({
        to: '/$language/subjects/$subjectId',
        params: { language, subjectId: destination },
      }).then(() => toast.show(t('management.subjectCreated')))
  }, [destination, navigate, language, toast, t])
  const input = () =>
    subject
      ? {
          subjectId: subject.id,
          version: subject.version,
          spanishName: values.spanishName,
          englishName: values.englishName,
          active: values.active,
          categoryCodes: values.categoryCodes,
          reason: values.reason,
        }
      : values
  function validate() {
    const schema = subject ? adminUpdateSubjectInput : adminCreateSubjectInput
    const result = schema.safeParse(input())
    setErrors(result.success ? {} : fieldErrors(result.error.issues, t))
    if (!result.success) invalid.request()
    return result.success
  }
  /**
   * Delete the subject, confirmed in its dialog. Resolves true only once the
   * server has deleted it; anything else is said inside the dialog, with the
   * reason still in its field.
   */
  async function deleteSubject(): Promise<boolean> {
    if (!subject || lock.current) return false
    const parsed = adminDeleteSubjectInput.safeParse({
      subjectId: subject.id,
      version: subject.version,
      reason: deleteReason,
    })
    if (!parsed.success) {
      setDeleteOutcome({ refusal: t('management.errors.reason') })
      return false
    }
    lock.current = true
    setDeleteOutcome(undefined)
    try {
      const result = (await remove({ variables: { input: parsed.data } })).data?.adminDeleteSubject
      if (!result?.deleted) {
        setDeleteOutcome({ refusal: userError(result?.userErrors, t('management.failed')) })
        return false
      }
      setSuccess(true)
      setDestination('list')
      return true
    } catch (caught) {
      setDeleteOutcome({ error: caught })
      return false
    } finally {
      lock.current = false
    }
  }
  async function submit() {
    if (lock.current || !validate()) return
    lock.current = true
    setBusy(true)
    setError(null)
    setFailure(undefined)
    try {
      const result = subject
        ? (await update({ variables: { input: adminUpdateSubjectInput.parse(input()) } })).data
            ?.adminUpdateSubject
        : (await create({ variables: { input: adminCreateSubjectInput.parse(input()) } })).data
            ?.adminCreateSubject
      if (!result?.subject) {
        setError(userError(result?.userErrors, t('management.failed')))
        return
      }
      setSuccess(true)
      onSaved?.()
      if (!subject) setDestination(result.subject.id)
    } catch (caught) {
      setFailure(caught)
    } finally {
      lock.current = false
      setBusy(false)
    }
  }
  const set = (key: 'code' | 'spanishName' | 'englishName' | 'reason', value: string) => {
    setValues((v) => ({ ...v, [key]: value }))
    setSuccess(false)
  }
  const name = subject ? (language === 'en' ? subject.englishName : subject.spanishName) : null
  return (
    <Section
      id="subject-management"
      // The page's heading in both cases: the subject's page is this form.
      level={1}
      title={name ?? t('management.createSubject')}
      className="w-full max-w-3xl"
    >
      <UnsavedChanges
        dirty={!success && !destination && JSON.stringify(values) !== JSON.stringify(initial)}
      />
      {subject ? (
        <div className="-mt-2 flex flex-wrap items-center gap-2 text-body-sm text-text-secondary">
          <span className="font-mono">{subject.code}</span>
          <SubjectStatusTag active={subject.active} />
          <span>
            {countOf(t, subject.practiceCount, 'management.usageOne', 'management.usageOther')}
          </span>
        </div>
      ) : null}
      {confirmation}
      {/* A notice in error tone is an alert already; no second one around it. */}
      {error ? <Notice tone="error" title={error} /> : null}
      {failure === undefined ? null : (
        <FailureNotice title={t('management.notSaved')} error={failure} />
      )}
      <form
        ref={invalid.ref}
        // Athena says what is missing in its own words; the browser's
        // tooltip would say it a second time, differently.
        noValidate
        className="flex w-full flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          // A routine save asks nothing: the reason is the deliberate step, and
          // the audit log keeps it.
          void submit()
        }}
      >
        <fieldset disabled={busy || success} className="flex min-w-0 flex-col gap-5">
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <h2 className="text-h4 font-semibold text-text sm:col-span-2">
              {t('admin.subjectDetails')}
            </h2>
            <div className="sm:col-span-2 sm:max-w-sm">
              <TextInput
                label={t('management.code')}
                helperText={t('management.codeHelp')}
                value={values.code}
                readOnly={!!subject}
                onChange={(e) => set('code', e.target.value)}
                required
                maxLength={64}
                error={errors['code'] ?? ''}
              />
            </div>
            <TextInput
              label={t('management.spanishName')}
              value={values.spanishName}
              onChange={(e) => set('spanishName', e.target.value)}
              required
              maxLength={120}
              error={errors['spanishName'] ?? ''}
            />
            <TextInput
              label={t('management.englishName')}
              value={values.englishName}
              onChange={(e) => set('englishName', e.target.value)}
              required
              maxLength={120}
              error={errors['englishName'] ?? ''}
            />
          </div>

          <fieldset className="flex min-w-0 flex-col gap-2 border-t border-border pt-5">
            <legend className="text-h4 font-semibold text-text">
              {t('subjectCategories.title')}
            </legend>
            <p className="text-body-sm text-text-secondary">
              {t('subjectCategories.membershipHelp')}
            </p>
            <div className="grid gap-x-4 sm:grid-cols-2">
              {SUBJECT_CATEGORY_CODES.map((code) => (
                <Checkbox
                  key={code}
                  density="compact"
                  label={t(`subjectCategories.names.${code}`)}
                  checked={values.categoryCodes.includes(code)}
                  onCheckedChange={(checked) => {
                    setValues((v) => ({
                      ...v,
                      categoryCodes:
                        checked === true
                          ? [...v.categoryCodes, code]
                          : v.categoryCodes.filter((value) => value !== code),
                    }))
                    setSuccess(false)
                  }}
                />
              ))}
            </div>
          </fieldset>

          {/* Lifecycle apart from the names: switching a subject off changes
              what tutors can file new practices under, which renaming it does
              not. */}
          <div className="flex flex-col gap-2 border-t border-border pt-5">
            <h2 className="text-h4 font-semibold text-text">{t('admin.subjectAvailability')}</h2>
            <Checkbox
              label={t('admin.subjectAvailable')}
              checked={values.active}
              onCheckedChange={(checked) => {
                setValues((v) => ({ ...v, active: checked === true }))
                setSuccess(false)
              }}
            />
            <p className="max-w-prose text-body-sm text-text-secondary">
              {t('management.subjectEffect')}
            </p>
          </div>

          <div className="border-t border-border pt-5">
            <ReasonField
              value={values.reason}
              onChange={(value) => set('reason', value)}
              error={errors['reason'] ?? ''}
            />
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={busy || success} loading={busy}>
            {t(subject ? 'management.save' : 'management.createSubject')}
          </Button>
        </div>
      </form>
      {subject ? (
        <div id="subject-audit" className="border-t border-border pt-6">
          <CollapsibleSection headingLevel={2} title={t('admin.auditTitle')}>
            <ResourceAuditLog resourceType="Subject" resourceId={subject.id} />
          </CollapsibleSection>
        </div>
      ) : null}
      {subject ? (
        /* Last, apart from Save, under its own heading: deleting is not
           another way of finishing the form, and two equal buttons side by
           side said it was. */
        <div className="mt-4 flex flex-col gap-3 border-t border-border pt-6">
          <h2 className="text-h3 font-semibold text-text">{t('management.dangerZone')}</h2>
          <div className="flex flex-col gap-4 rounded-md border border-error-border bg-surface p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-body-sm font-semibold text-text">
                {t('management.deleteSubject')}
              </p>
              <p className="mt-1 max-w-prose text-body-sm text-text-secondary">
                {t('management.deleteSubjectEffect', [subject.code])}
              </p>
            </div>
            <Button
              type="button"
              variant="destructive"
              className="w-full shrink-0 sm:w-auto"
              disabled={busy || success}
              onClick={() => {
                setDeleteOutcome(undefined)
                setDeleting(true)
              }}
            >
              {t('management.deleteSubject')}
            </Button>
          </div>
          <ConfirmationDialog
            open={deleting}
            onOpenChange={setDeleting}
            intent="destructive"
            title={t('management.deleteSubjectTitle', [subject.code])}
            description={t('management.deleteSubjectBody', [subject.code])}
            confirmLabel={t('management.deleteSubjectPermanently')}
            confirmingLabel={t('management.deleting')}
            confirmDisabled={deleteReason.trim() === ''}
            cancelLabel={t('management.keepSubject')}
            failure={
              deleteOutcome === undefined ? null : 'refusal' in deleteOutcome ? (
                <Notice tone="error" title={deleteOutcome.refusal} />
              ) : (
                <FailureNotice title={t('management.notSaved')} error={deleteOutcome.error} />
              )
            }
            onConfirm={deleteSubject}
          >
            <ReasonField value={deleteReason} onChange={setDeleteReason} />
          </ConfirmationDialog>
        </div>
      ) : null}
    </Section>
  )
}
